import {StoreHelpers} from "../StoreHelpers";
import {Logger} from "../Logger";
import {E_GreenSocket_DisconnectReason, E_GreenSocket_MessageTypes, E_GreenSocket_UnsubscribeReason} from "../../models/constants/GreenSocketConstants";
import {RefresherV2} from "../API/Refresher";
import {GreenSocketConnection} from "./SocketConnection";
import {resolvePolymorphVar} from "../../functions/generic";
import {M_GreenSocketOptions} from "../../models/Models_GreenSocket";
import {ResponseError} from "../API/ResponseError/ResponseError";

const logger = new Logger("GreenSocketV2");

export class GreenSocketV2 {
	/**
	 *  @type {WebSocket}
	 */
	socket;
	disconnectReason;
	/**
	 * @type {GreenSocketConnection}
	 */
	#publicConnection;
	/**
	 * @type {GreenSocketConnection}
	 */
	#privateConnection;

	/**
	 * Refresher instance that triggers a reconnection after the connection was lost
	 */
	#disconnectedRefresher;

	/**
	 * Connection URL address
	 */
	#url;
	#authToken;
	#authorized;
	/**
	 * @type {M_GreenSocketOptions}
	 */
	#options;

	#manualDisconnect = false;

	/**
	 * @param {M_GreenSocketOptions} options
	 */
	constructor(options) {
		this.options = options;

		this.#publicConnection = new GreenSocketConnection(this, "public");
	}

	static get enum() {
		return {
			unsubscribeReason: E_GreenSocket_UnsubscribeReason,
			messageTypes: E_GreenSocket_MessageTypes,
			disconnectReason: E_GreenSocket_DisconnectReason,
			error: E_GreenSocket_Error,
		}
	}

	set options(options) {
		this.#options = StoreHelpers.deepMerge(
			StoreHelpers.deepClone(M_GreenSocketOptions),
			options,
		);
	}

	/**
	 * @return {M_GreenSocketOptions}
	 */
	get options() {
		return this.#options;
	}

	/**
	 * Is connected
	 * ---
	 * Returns whether the socket is actually connected
	 * @return {boolean}
	 */
	get isConnected() {
		return this.socket?.readyState == WebSocket.OPEN;
	}

	/**
	 * Is authorized
	 * ---
	 * Returns whether the socket is connected and is authorized to send messages.
	 * **Notice** If an auth token is not present, authorized = connected.
	 * @return {boolean}
	 */
	get isAuthorized() {
		return this.isConnected && (this.#authToken ? this.#authorized : true) || false;
	}

	/**
	 * Connect
	 * ---
	 * Connects this socket instance to the url.
	 * @param {string} url Socket Url
	 * @param {true|string|function(): string} authToken Authorization token. true = manual authorization required; string = token; function = token getter
	 * @param {function(): void} onConnectionError Connection error callback. When a connection error occurs, this callback will be called.
	 */
	connect(url = this.#url, authToken = this.#authToken, onConnectionError = () => null) {
		if(!url) {
			console.error(`Url for WS connection is invalid!`, url);

			onConnectionError(E_GreenSocket_Error.INVALID_URL);

			return;
		}

		if(this.isConnected) {
			this.disconnect(E_GreenSocket_DisconnectReason.NEW_CONNECTION);
		}

		this.#url = url;
		this.#authToken = authToken;

		logger.isTypeAllowed("info") && console.info(`⬆️ Connecting to: "${url}"...`);

		let socket;
		try {
			this.socket = socket = new WebSocket(url);
		}
		catch (e) {
			let res = new ResponseError({
				statusCode: e.code,
				error: e.name,
				message: e.message,
			}, e);

			console.error(`❌ Connection to "${url}" failed.`, res);

			onConnectionError(res, e);

			this._triggerRefresher();

			return;
		}

		//on connection error
		socket.onerror = () => {
			let res = new ResponseError({
				error: "CONNECTION_ERROR",
				message: `An unexpected error has occurred.`,
			});

			onConnectionError(res, undefined);
		};

		//on open
		socket.addEventListener("open", e => {
			logger.isTypeAllowed("info") && console.info(`✅ Connection established`, e);

			this.#disconnectedRefresher?.stop();

			this.#privateConnection = new GreenSocketConnection(this, "private");

			this.#publicConnection.processConnected(e);
			this.#privateConnection.processConnected(e, this.#privateConnection);

			//If authToken is defined, try to log in.
			if(this.#authToken) {
				if(this.#authToken === true) {
					//Do nothing. Manual authorization required.
				}
				else {
					this.authorize(this.#authToken);
				}
			}
			else {
				this._completeAuthorizationAfterConnect(e);
			}
		});

		//on close
		socket.addEventListener("close", e => {
			logger.isTypeAllowed("info") && console.info(`🚪 Socket closed:`, e);

			this.#authorized = false;

			this.#publicConnection.processDisconnected(e, this.#manualDisconnect, this.disconnectReason);
			this.#privateConnection?.processDisconnected(e, this.#manualDisconnect, this.disconnectReason);

			if(!this.#manualDisconnect) {
				this._triggerRefresher();
			}
		});

		//on message
		socket.addEventListener("message", e => {
			let payload;
			try {
				payload = JSON.parse(e.data);
			} catch (e) {
				console.error(`❌ Failed to parse message payload`);
			}

			logger.isTypeAllowed("info") && console.info(`❇️ Message received:\n`, payload);

			this.#publicConnection.processMessage(payload, e);
			this.#privateConnection.processMessage(payload, e, this.#privateConnection);

			//If the message is of type LOGIN
			if(this.#authToken && payload.type == E_GreenSocket_MessageTypes.LOGIN) {
				logger.isTypeAllowed("info") && console.info('🔓 Logged in');

				this._completeAuthorizationAfterConnect();
			}

			if(payload.type == E_GreenSocket_MessageTypes.UNSUBSCRIBE) {
				logger.isTypeAllowed("info") && console.info(`❇️ Unsubscribe message received.`, payload);

				this.unsubscribeTopic(payload.destination, payload);
			}
			else if(payload.type == E_GreenSocket_MessageTypes.ERROR) {
				logger.isTypeAllowed("error") && console.error(`❌ Error message "${payload.data.error}" received:`, payload);
			}
		});
	}

	/**
	 * Authorize
	 * ---
	 * Tries to authorize the socket. If the authorization succeeds, a message of type LOGIN will be received, confirming the authorization.
	 * From that point forward, the socket is authorized, and is permitted to send messages.
	 * @param authToken
	 */
	authorize(authToken = this.#authToken) {
		this.#authToken = authToken;

		logger.isTypeAllowed("info") && console.info(`🔓 Logging in using token "${authToken}"`);

		this.sendMessage(E_GreenSocket_MessageTypes.LOGIN, {
			"x-auth-token": resolvePolymorphVar(
				authToken,
				{
					string: s => s,
					function: f => f(),
				},
				undefined,
				true
			),
		}, undefined, undefined, false);
	}

	/**
	 * Deauthorize
	 * ---
	 * De-authorizes (locally) the socket.
	 * @param reason
	 */
	deauthorize(reason) {
		logger.isTypeAllowed("info") && console.info(`🔒 Logging out...\nReason: ${reason}`);

		this.#authorized = false;
		this.#authToken = this.#authToken ? true : undefined;
	}

	/**
	 * Disconnect
	 * ---
	 * Disconnects the socket and removes it's instance.
	 * @param {string|*} reason
	 */
	disconnect(reason = undefined) {
		this.#manualDisconnect = true;
		this.disconnectReason = reason;

		this.socket.close();

		this.#disconnectedRefresher?.stop();

		delete this.socket;
	}

	/**
	 * On event
	 * ---
	 * Add a listener to a specified event, type and destination based on the arguments provided.
	 * @param {"connected"|"authorized"|"disconnected"|"message"|"error"|string} eventName Name of the event or a destination to listen to. If the value is not one of the predetermined, it's considered a destination.
	 * @param {function(payload: Object|Event): void} listener
	 * @param {E_GreenSocket_MessageTypes} type Optional type to match
	 * @param {boolean} replacePrevious Whether to replace any previous "on" listeners based on eventName, destination and type.
	 */
	on(eventName, listener, {type, replacePrevious} = {}) {
		return this.#publicConnection.on(eventName, listener, {type, replacePrevious});
	}

	/**
	 * Send message
	 * ---
	 * Sends a message.
	 * @param {E_GreenSocket_MessageTypes} type Message type.
	 * @param {*} data
	 * @param {string} destination
	 * @param {string} id Custom ID
	 * @param {boolean} pushToQueueOnFail Whether to push the message to the queue if it fails to sent for any reason. (e.g. connection is not established or authorized)
	 */
	sendMessage(type, data, destination, id, pushToQueueOnFail) {
		this.#publicConnection.sendMessage(type, data, destination, {id, pushToQueueOnFail});
	}

	/**
	 * Subscribe topic
	 * ---
	 * @param {string} topic
	 * @param {function(reason: null|*): void} unsubscribeCallback
	 * @param {boolean} resubscribe
	 */
	subscribeTopic(topic, unsubscribeCallback = () => null, resubscribe = true) {
		this.#publicConnection.subscribeTopic(topic, unsubscribeCallback, resubscribe);
	}

	/**
	 * Unsubscribe topic
	 * ---
	 * @param {string} topic
	 * @param {*} reason Internal reason for the unsubscription.
	 * @param {string} sentReason A reason that will be sent to the backend along the unsubscribe request.
	 */
	unsubscribeTopic(topic, reason = undefined, sentReason = undefined) {
		this.#publicConnection.unsubscribeTopic(topic, reason, sentReason);
	}

	/**
	 * @private
	 * Trigger refresher
	 * ---
	 * Triggers or creates a refresher if there is none.
	 */
	_triggerRefresher() {
		logger.isTypeAllowed("info") && console.info(`🔄️ Triggering refresher.`);

		if(this.#disconnectedRefresher) {
			if(this.#disconnectedRefresher.isStopped) {
				this.#disconnectedRefresher.reset();
			}

			this.#disconnectedRefresher.trigger();
		}
		else {
			this._startRefresher();
		}
	}

	/**
	 * @private
	 * Start refresher
	 * ---
	 */
	_startRefresher() {
		this.#disconnectedRefresher = new RefresherV2(
			() => {
				logger.isTypeAllowed("info") && console.info(`🔄️ Attempting to reconnect...`);

				this.connect()
			},
			this.options.disconnectedRefreshTime,
			{
				callbackOnConstruct: false,
				failedAttemptsDelayCurve: this.options.refreshDelayCurve,
			}
		);
	}

	/**
	 * @private
	 * Complete authorization after connect
	 * ---
	 * This completes the authorization process when a LOGIN message is received or the socket is connected without any token specified (authToken must not be true|string)
	 */
	_completeAuthorizationAfterConnect() {
		this.#authorized = true;

		if(this.options.resubscribeAfterReconnect) {
			this.#publicConnection.resubscribe();
		}
		this.#publicConnection.submitQueue();
	}
}
